import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDocumentsQuery, useProjectsQuery } from '../../hooks/queries';
import { useCreateDocument } from '../../hooks/mutations';
import { useEntitlements } from '../../hooks/useEntitlements';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, Plus, Search, Clock, Folder, Presentation, Globe, ChevronDown, Network, History, Database, X, Star } from 'lucide-react';
import MobileFAB from '../../components/mobile/MobileFAB';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../components/ui/drawer';
import { asUtcIfNaive } from '../../lib/serverDate';

const DOC_TYPE_LIST = [
  { type: 'document', icon: FileText, color: 'text-blue-400' },
  { type: 'folder', icon: Folder, color: 'text-yellow-400' },
  { type: 'database', icon: Database, color: 'text-cyan-400' },
  { type: 'whiteboard', icon: Presentation, color: 'text-green-400' },
  { type: 'mindmap', icon: Network, color: 'text-pink-400' },
  { type: 'embed', icon: Globe, color: 'text-purple-400' },
  { type: 'changelog', icon: History, color: 'text-amber-700 dark:text-amber-400' },
];
// Every plan has rich-text documents and folders; the other types need a paid plan
// (backend/routes/documents.py FREE_DOC_TYPES).
const FREE_TYPES = new Set(['document', 'folder']);

const DocumentsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { limitOf, has } = useEntitlements();
  const documentsQuery = useDocumentsQuery();
  const projectsQuery = useProjectsQuery();
  const documents = documentsQuery.data || [];
  const projects = projectsQuery.data || [];
  const loading = documentsQuery.isPending || projectsQuery.isPending;
  const createDocument = useCreateDocument();
  const [search, setSearch] = useState('');
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showMobileCreate, setShowMobileCreate] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const menuRef = useRef(null);
  const DOC_TYPES = DOC_TYPE_LIST.map((d) => ({
    ...d,
    label: t(`projects.doc_types.${d.type}`),
    desc: t(`documents.type_desc.${d.type}`),
  }));
  const getDocTypeInfo = (type) => DOC_TYPES.find((d) => d.type === type) || DOC_TYPES[0];
  const isLocked = (type) => !FREE_TYPES.has(type) && !has('all_document_types');

  // Close create menu on outside click
  useEffect(() => {
    if (!showCreateMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowCreateMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCreateMenu]);

  const handleCreate = async (type = 'document') => {
    const docCap = limitOf('documents');
    if (docCap !== null && documents.length >= docCap) {
      window.dispatchEvent(new CustomEvent('upgradNeeded', {
        detail: { resource: 'documents', message: t('ui_pages.limits.documents_limit', { count: docCap }) },
      }));
      setShowCreateMenu(false);
      return;
    }
    try {
      // No title yet: every reader sees "Untitled" in their own language.
      const doc = await createDocument.mutateAsync({
        title: '',
        type,
        content: type === 'document' ? '' : null,
      });
      navigate(`/app/documents/${doc.id}`, { state: { nameIt: true } });
    } catch (err) {
      console.error('Failed to create document:', err);
    }
    setShowCreateMenu(false);
  };

  const getProjectName = (projectId) => {
    const p = projects.find(p => p.id === projectId);
    return p?.name || null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(asUtcIfNaive(dateStr));
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return t('documents.just_now');
    if (diff < 3600000) return t('documents.minutes_ago', { count: Math.floor(diff / 60000) });
    if (diff < 86400000) return t('documents.hours_ago', { count: Math.floor(diff / 3600000) });
    if (diff < 604800000) return t('documents.days_ago', { count: Math.floor(diff / 86400000) });
    return d.toLocaleDateString(i18n.language);
  };

  const filtered = documents
    .filter(d => !search || d.title?.toLowerCase().includes(search.toLowerCase()))
    .filter(d => !filterType || (d.type || 'document') === filterType)
    .filter(d => !favoritesOnly || d.is_favorite)
    .sort((a, b) => {
      // Favorites pin to the top (matches the Projects page), then most
      // recently updated first.
      const favA = a.is_favorite ? 0 : 1;
      const favB = b.is_favorite ? 0 : 1;
      if (favA !== favB) return favA - favB;
      return new Date(asUtcIfNaive(b.updated_at || b.created_at)) - new Date(asUtcIfNaive(a.updated_at || a.created_at));
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="hidden md:flex text-2xl font-bold text-foreground items-center gap-3">
            <FileText className="w-7 h-7 text-blue-400" />
            {t('nav.documents')}
          </h1>
          <p className="text-muted-foreground md:mt-1">{t('documents.document_count', { count: documents.length })}</p>
        </div>
        <div className="relative hidden md:block" ref={menuRef}>
          <Button
            data-tour="documents-new"
            onClick={() => setShowCreateMenu(!showCreateMenu)}
            className="bg-amber-500 text-black hover:bg-amber-400"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('common.new')}
            <ChevronDown className="w-3.5 h-3.5 ml-1" />
          </Button>
          {showCreateMenu && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-card border border-border rounded-lg shadow-xl z-50 py-1">
              {DOC_TYPES.map(({ type, label, icon: Icon, color, desc }) => {
                const locked = isLocked(type);
                return (
                  <button
                    key={type}
                    onClick={() => !locked && handleCreate(type)}
                    disabled={locked}
                    className={`w-full flex items-center gap-3 px-3 py-2 transition-colors text-left ${locked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent'}`}
                  >
                    <Icon className={`w-4 h-4 ${color}`} />
                    <div className="flex-1">
                      <div className="text-sm text-foreground">{label}</div>
                      <div className="text-[11px] text-muted-foreground">{locked ? t('common.upgrade_required') : desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 md:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('documents.search_placeholder')}
            className="pl-10 pr-9 bg-card border-border"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              title={t('common.clear')}
              aria-label={t('common.clear')}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto bb-no-scrollbar">
          <button
            onClick={() => { setFilterType(null); setFavoritesOnly(false); }}
            className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              !filterType && !favoritesOnly ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            {t('common.all')}
          </button>
          <button
            onClick={() => setFavoritesOnly(!favoritesOnly)}
            title={t('nav.favorites')}
            className={`shrink-0 whitespace-nowrap flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              favoritesOnly ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            <Star className={`w-3 h-3 ${favoritesOnly ? 'text-amber-700 dark:text-amber-400 fill-amber-400' : ''}`} />
            {t('nav.favorites')}
          </button>
          {DOC_TYPES.map(({ type, label, icon: Icon, color }) => (
            <button
              key={type}
              onClick={() => setFilterType(filterType === type ? null : type)}
              className={`shrink-0 whitespace-nowrap flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterType === type ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Icon className={`w-3 h-3 ${filterType === type ? 'text-amber-700 dark:text-amber-400' : color}`} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Documents Grid */}
      {filtered.length === 0 ? (
        <div className="bg-card rounded-xl border border-border text-center py-16">
          {favoritesOnly && !search && !filterType ? (
            <>
              <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t('documents.no_favorites')}</h3>
              <p className="text-muted-foreground mb-4">{t('documents.no_favorites_hint')}</p>
            </>
          ) : (
            <>
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {search || filterType || favoritesOnly ? t('documents.no_documents') : t('documents.no_documents_yet')}
              </h3>
              <p className="text-muted-foreground mb-4">
                {search || filterType || favoritesOnly ? t('documents.try_different_search') : t('documents.create_first')}
              </p>
              {!search && !filterType && !favoritesOnly && (
                <Button onClick={() => handleCreate('document')} variant="outline" className="border-border text-muted-foreground hover:text-foreground">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('documents.new_document')}
                </Button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((doc) => {
            const typeInfo = getDocTypeInfo(doc.type || 'document');
            const DocIcon = typeInfo.icon;
            return (
              <button
                key={doc.id}
                onClick={() => navigate(`/app/documents/${doc.id}`)}
                className="text-left bg-card border border-border rounded-xl p-5 hover:border-amber-500/30 hover:bg-accent transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <DocIcon className={`w-8 h-8 ${typeInfo.color} opacity-60 group-hover:opacity-100 transition-opacity`} />
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-muted/50 ${typeInfo.color}`}>
                    {typeInfo.label}
                  </span>
                </div>
                <h3 className="font-medium text-foreground mb-1 flex items-center gap-1.5 min-w-0">
                  <span className="truncate">{doc.title || t('documents.untitled')}</span>
                  {doc.is_favorite && (
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
                  )}
                </h3>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {doc.project_id && (
                    <span className="flex items-center gap-1">
                      <Folder className="w-3 h-3" />
                      {getProjectName(doc.project_id)}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(doc.updated_at || doc.created_at)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <MobileFAB onClick={() => setShowMobileCreate(true)} label={t('documents.new_document')} />

      <Drawer open={showMobileCreate} onOpenChange={setShowMobileCreate}>
        <DrawerContent className="bg-card">
          <DrawerHeader>
            <DrawerTitle>{t('documents.new_document')}</DrawerTitle>
          </DrawerHeader>
          <div className="px-2 pb-[max(env(safe-area-inset-bottom),16px)] overflow-y-auto flex flex-col gap-0.5">
            {DOC_TYPES.map(({ type, label, icon: Icon, color, desc }) => {
              const locked = isLocked(type);
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => { if (!locked) { handleCreate(type); setShowMobileCreate(false); } }}
                  disabled={locked}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left ${locked ? 'opacity-50' : 'active:bg-accent/70 hover:bg-accent/40'}`}
                >
                  <span className="w-10 h-10 rounded-lg bg-muted/60 flex items-center justify-center flex-shrink-0">
                    <Icon className={`w-5 h-5 ${color}`} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-foreground font-medium">{label}</div>
                    <div className="text-[12px] text-muted-foreground truncate">{locked ? t('common.upgrade_required') : desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default DocumentsPage;
